import { Component, Element, Listen, State, getAssetPath, h, Event, EventEmitter, Prop } from '@stencil/core'
import { autosuggest, retailSearchResultsPage } from 'src/global/resources'
import { request } from 'src/utils/http-requests.util'
import { searchResultClick } from 'src/model/search-result-clicked-object.model'
import { Autosuggest, AutosuggestResult } from 'src/model/autosuggest.model'

// eslint-disable-next-line @typescript-eslint/consistent-type-definitions, @typescript-eslint/no-explicit-any
declare global {interface Window { dataLayer?: any[] }}

export type SearchEvent = {
  searchTerm: string
  result: AutosuggestResult[]
}

@Component({
  tag: 'tradelab-search-widget',
  styleUrl: 'tradelab-search-widget.scss',
  shadow: true,
  assetsDirs: ['assets'],
})
export class TradelabSearchWidget {
  @State() searchQuery = ''
  @State() onDropdown = false
  @State() autosuggestions: AutosuggestResult[] | null = null
  @State() recentSearches: string[] = []
  @Prop() filterSearchParams: string[][] | Record<string, string> | string | URLSearchParams = ''
  /** How many items to display on the dropdown */
  @Prop() dropdownMaxItems = 5
  /** How many items of each 'types' to fetch from the API. Ignored if 'limits' parameter is present */
  @Prop() apiFetchMaxItems = 10

  @Element() el: HTMLElement
  tradelabSearchInput!: HTMLInputElement

  @Event({ bubbles: true }) previousNavigationEvent: EventEmitter<string>
  @Event({ bubbles: true }) searchResultClicked: EventEmitter<searchResultClick>
  @Event({ bubbles: true }) search: EventEmitter<SearchEvent>

  // Lifecycle Methods
  componentDidLoad() {
    this.bodyScrollDisabled(window.innerWidth <= 990)
    this.loadRecentSearches()
  }

  disconnectedCallback() {
    this.bodyScrollDisabled(false) // Ensure scrolling is enabled when the component is removed
  }

  // Event listeners
  @Listen('focusout')
  protected handleFocusOut(event: FocusEvent) {
    const target = event.target as HTMLElement
    if (target.shadowRoot == this.el.shadowRoot) {
      this.bodyScrollDisabled(false)
      this.onDropdown = false
    }
  };

  @Listen('keydown')
  protected handleKeyDown(event: KeyboardEvent) {
    const tradelabSearchWidgetDropdown = this.el.shadowRoot.querySelector('tradelab-search-dropdown')
    const dropdownItems = tradelabSearchWidgetDropdown.shadowRoot.querySelectorAll('.search-widget-dropdown-list-item')
    const focusedElement = tradelabSearchWidgetDropdown.shadowRoot.activeElement
    const currentIndex = Array.from(dropdownItems).indexOf(focusedElement as HTMLElement)

    if (event.key === 'Escape') {
      this.bodyScrollDisabled(false)
      this.onDropdown = false
      if ('blur' in event.target) (event.target as HTMLInputElement).blur()
    }

    if (event.key === 'ArrowDown') {
      if (currentIndex >= 0 && currentIndex < dropdownItems.length - 1) {
        console.log('currentIndex ArrowDown => ', currentIndex);
        (dropdownItems[currentIndex + 1] as HTMLElement).focus()
      } else if (currentIndex === -1 && dropdownItems.length > 0) {
        (dropdownItems[0] as HTMLElement).focus()
      }
    }
    if (event.key === 'ArrowUp') {
      if (currentIndex > 0 && currentIndex <= dropdownItems.length) {
        (dropdownItems[currentIndex - 1] as HTMLElement).focus()
      } else if (currentIndex === 0 && dropdownItems.length > 0) {
        this.tradelabSearchInput.focus()
      }
    }
  };

  // load recentSearches from local storage
  loadRecentSearches() {
    const searchSession = window.localStorage.getItem('recentSearches')
    if (searchSession) {
      // recentSearches in the local storage are in this format: recentSearches=aapl},{goog
      // recent searches seperator is },{
      this.recentSearches = searchSession.split('},{')
    }
  }

  handleUpdateRecentSearches(searchTerm: string) {
    // if user input does not contain atleast one letter or number, do not add it to recent searches
    if (searchTerm.match('.*[a-zA-Z0-9].*') !== null) {
      const checkedExpression = searchTerm
        .replace(/^=+/, '') // ignore all leading equal signs
        .replaceAll('},{', '')
        .trim()
        .replace(/<[^>]*>?/gm, '')
      if (checkedExpression !== '' && !this.recentSearches.includes(checkedExpression)) {
        this.recentSearches = [checkedExpression, ...this.recentSearches] // adds search term to recent search array
      }
    }

    if (this.recentSearches.length > 0) {
      this.recentSearches = this.recentSearches.slice(0, 5) // Limit recent searches to 5
      window.localStorage.recentSearches = this.recentSearches.join('},{') // Add recent searches to user's local storage
    }
  }

  handleBackButton() {
    this.bodyScrollDisabled(false)
    this.previousNavigationEvent.emit('backArrow')
  }

  handleClearInput() {
    this.searchQuery = ''
    this.tradelabSearchInput.focus()
  }

  async handleTradelabSearchInputChange(event: InputEvent) {
    this.searchQuery = (event.target as HTMLInputElement).value
    if (this.searchQuery.length > 0) {
      this.onDropdown = true
      const params = new URLSearchParams(this.filterSearchParams)
      params.set('query', this.searchQuery)

      const types = params.getAll('types').flatMap(t => t.split(','))
      if (types.length > 0) {
        params.set('types', types.join(','))
      } else {
        console.warn('\'types\' parameter is mandatory')
      }

      if (!params.has('limits') && types.length > 0) {
        const limits = Array(types.length)
          .fill(this.apiFetchMaxItems)
          .join(',')
        params.set('limits', limits)
      }
      try {
        const res = await request<Autosuggest>(autosuggest, { params })
        if (!res.ok) throw new Error(await res.text())
        this.autosuggestions = (await res.json()).results
      } catch (error) {
        console.error('Error fetching autosuggest values:', (error as Error).message)
      }
    } else {
      this.autosuggestions = null
      if (this.recentSearches.length > 0) this.onDropdown = true
    }
  }

  handleSearch() {
    if (this.searchQuery == '') return
    this.bodyScrollDisabled(false)
    this.handleUpdateRecentSearches(this.searchQuery)
    this.search.emit({
      searchTerm: this.searchQuery,
      result: this.autosuggestions,
    })
  }

  handleFocus() {
    // Opening dropdown on focus if there is recent searches
    if (this.recentSearches.length > 0) this.onDropdown = true
    this.bodyScrollDisabled(window.innerWidth <= 990)
  }

  bodyScrollDisabled(disable: boolean) {
    document.body.style.overflow = disable ? 'hidden' : ''
  }

  createSearchClickObject(searchTermClicked: string, searchType: string) {
    console.debug('create search click object clicked')
    const searchResultClickObj = {
      event: 'searchResultClicked',
      searchInfo: {
        resultClicked: searchTermClicked, // evar 41
        searchTermEntered: this.searchQuery, // (c27,v27)
        searchType: searchType, // autosuggest or global (v91)
      },
    }
    this.searchResultClicked.emit(searchResultClickObj)
  }

  render() {
    return (
      <div id="search-widget">
        <div
          class="modal-overlay-bg"
          onClick={() => {
            this.handleBackButton()
            this.onDropdown = false
          }}
        />
        <div class="search-widget-container">
          <div class="search-input-container">
            <button
              type="button"
              class="back-button"
              onClick={() => this.handleBackButton()}
              aria-label="close search"
            >
              <img src={getAssetPath('./assets/left-arrow.png')} alt="close search" />
            </button>
            <div
              role="search"
              class="c11n-search c11n-search--medium c11n-search--below c11n-search--attached c11n-search--on-light c11n-search--cols3 c11n-dropdown__trigger c11n-menu__trigger"
            >
              <form
                class="c11n-search__form"
                method="get"
                action={`${retailSearchResultsPage}${this.searchQuery}`}
                onSubmit={(e) => {
                  e.preventDefault()
                  this.handleSearch()
                  this.handleBackButton()
                }}
              >
                <div class="c11n-search__menu__sr-only" id="text-input-id-input-description">press enter to search</div>
                <div class="c11n-search__form__input-row">
                  <div class="c11n-search__form__input-row__input">
                    <div class="c11n-input c11n-input--medium">
                      <input
                        autofocus
                        type="text"
                        ref={el => this.tradelabSearchInput = el!}
                        id="tradelabSearchInput"
                        aria-describedby="text-input-id-input-description"
                        value={this.searchQuery}
                        class="c11n-input__input"
                        onInput={e => this.handleTradelabSearchInputChange(e)}
                        placeholder="Search or get a quote"
                        onFocus={() => this.handleFocus()}
                      />
                      <div class="c11n-input__focus-element"></div>
                    </div>
                    <clear-search-button
                      show={this.searchQuery !== ''}
                      onClick={() => this.handleClearInput()}
                    />
                  </div>
                  <search-button
                    onClick={() => {
                      this.handleSearch()
                      this.handleBackButton()
                      this.createSearchClickObject('Search Magnifying Glass', 'autosuggest click - Magnifying Glass')
                    }}
                  />
                </div>
              </form>
            </div>
          </div>
          {this.onDropdown && (
            <tradelab-search-dropdown
              autosuggest={this.autosuggestions}
              searchQuery={this.searchQuery}
              dropdownMaxItems={this.dropdownMaxItems}
              recentSearches={this.recentSearches}
              handleUpdateRecentSearches={this.handleUpdateRecentSearches.bind(this)}
              previousNavigationEvent={this.previousNavigationEvent}
              createSearchClickObject={this.createSearchClickObject.bind(this)}
            />
          )}
        </div>
      </div>
    )
  }
}
